<script setup lang="ts">
import ExcelIcon from "@/assets/Images/ExcelIcon.png";
import Button from "@/components/Reusables/Buttons/Button.vue";
import FileUpload from "@/components/Reusables/Inputs/FileUpload.vue";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";
import { useModalStore } from "@/stores/modals/modals.store";
import { computed, ref } from "vue";
import { useToast } from "vue-toastification";
import { parseWasteSortingExcel } from "../utils/parseWasteSortingExcel";
import WasteSortingExcelImportUrls from "../constants/WasteSortingExcelImportUrls";
import type { GetProjectByIdQuery } from "@/graphql";

const props = defineProps<{
  project?: GetProjectByIdQuery["project_by_pk"];
}>();

const modals = useModalStore();

// The toolbar's upload button is already blocked for a Viewer and while a frozen revision is
// previewed; this covers a modal that was open when the guard changed. The template downloads stay
// available regardless — they are reference material, not a write.
const { isReadOnly: isRevisionReadOnly, blockedByPreview } = useProjectEditReadOnly();

const countryKey = computed(() => {
  const country = props.project?.location?.country?.toLowerCase() ?? "";
  return WasteSortingExcelImportUrls[country] ? country : "generic";
});

const availableReports = computed(() => WasteSortingExcelImportUrls[countryKey.value] ?? WasteSortingExcelImportUrls["generic"]);

const languageOptions = computed(() => availableReports.value.map((r) => ({ name: r.language, value: r.language })));

const selectedLanguage = ref(availableReports.value[0]?.language ?? "");

const selectedReport = computed(
  () => availableReports.value.find((r) => r.language === selectedLanguage.value) ?? availableReports.value[0]
);

const importErrorsModalIsHidden = ref<boolean>(true);
const isImporting = ref<boolean>(false);
const hasError = ref<boolean>(false);

const uploadResponse = ref<{ importRowErrors: string[] }>();
const selectedFile = ref<File | null>(null);

const onFilesUpdate = (files: File[]) => {
  selectedFile.value = files[0] ?? null;
  uploadResponse.value = undefined;
  hasError.value = false;
};

const onFileError = () => {
  useToast().error("Please use .xlsx files");
};

const handleImport = async () => {
  if (blockedByPreview()) return;
  if (!selectedFile.value) return;

  isImporting.value = true;
  hasError.value = false;

  try {
    const buffer = await selectedFile.value.arrayBuffer();
    const result = parseWasteSortingExcel(buffer);

    if (!result.success) {
      uploadResponse.value = { importRowErrors: result.errors };
      isImporting.value = false;
      hasError.value = true;
      return;
    }

    isImporting.value = false;
    modals.close(result.data);
  } catch (_) {
    isImporting.value = false;
    hasError.value = true;
    uploadResponse.value = {
      importRowErrors: ["Failed to parse the Excel file. Please check the file format."],
    };
  }
};
</script>

<template>
  <div class="w-full max-w-2xl">
    <div class="flex items-center justify-between">
      <h4 class="t-h4 text-type-default">Upload Excel file</h4>
    </div>
    <div class="text-text-color flex w-full flex-col gap-6 pt-4">
      <div class="flex flex-col gap-3">
        <span class="t-small text-slate-400">Download the template to bulk import waste sortings:</span>
        <div class="flex items-center gap-3">
          <a
            :href="selectedReport?.url"
            target="_blank"
            class="bg-button-default text-type-muted rounded-8 t-small-bold flex items-center gap-2 px-6 py-3 leading-none shadow-xs transition-colors hover:bg-slate-300">
            <font-awesome-icon
              icon="fa-solid fa-download"
              class="text-app-green-600" />
            Download Template
          </a>
          <div
            v-if="languageOptions.length > 1"
            class="rounded-8 flex items-center border border-slate-200 p-0.5">
            <button
              v-for="opt in languageOptions"
              :key="opt.value"
              class="rounded-8 t-caption px-3 py-1.5 transition-colors"
              :class="selectedLanguage === opt.value ? 'text-type-muted bg-slate-200' : 'text-slate-400 hover:text-slate-600'"
              @click="selectedLanguage = opt.value">
              {{ opt.name }}
            </button>
          </div>
        </div>
      </div>

      <FileUpload
        data-cy="excel-drop-zone"
        accept=".xlsx"
        :disabled="isRevisionReadOnly"
        :error-msg="hasError ? 'Could not process the file — see errors below' : undefined"
        @update:files="onFilesUpdate"
        @error="onFileError">
        <template #preview>
          <img
            class="w-1/2"
            :src="ExcelIcon"
            alt="Excel file" />
        </template>
      </FileUpload>

      <!-- File errors -->
      <div
        v-if="hasError"
        class="text-text-color flex flex-col gap-4 wrap-break-word">
        <div class="flex flex-col gap-4">
          <p class="t-body max-w-sm">Could not process the uploaded Excel file.</p>

          <hr />
          <p>Errors:</p>
          <ul class="text-app-red-500 t-small flex list-disc flex-col gap-1 pl-5">
            <li
              v-for="(error, idx) in uploadResponse?.importRowErrors"
              :key="idx">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>

      <div class="flex w-full justify-between">
        <Button
          color="button"
          label="Cancel"
          @click="modals.close()" />
        <RevisionGatedButton
          color="primary"
          :is-loading="isImporting"
          :label="isImporting ? 'Importing...' : 'Import'"
          :guard="useProjectEditReadOnly"
          data-cy="import-excel-btn"
          @click="handleImport" />
      </div>
    </div>

    <div
      v-if="!importErrorsModalIsHidden && uploadResponse"
      class="border-app-red-500 rounded-8 bg-app-red-50 mt-4 max-h-48 overflow-y-auto border p-4">
      <ul class="text-app-red-500 t-small flex flex-col gap-1">
        <li
          v-for="(error, idx) in uploadResponse.importRowErrors"
          :key="idx">
          {{ error }}
        </li>
      </ul>
    </div>
  </div>
</template>
